import L from "leaflet";
import cn from "classnames";
import { useCallback, useMemo, useEffect } from "react";
import { renderToString } from "react-dom/server";
import { Marker, Polygon } from "react-leaflet";
import { useDispatch, useSelector } from "react-redux";

import features from "features";
import { LayerAlias, UnitTypes } from "constant";
import { convertPercentagesToNumber } from "helpers/convertPercentagesToNumber";
import { RootStateInterface } from "reducer";
import { UnitsManagerStateInterface } from "scenes/UnitsManager/interfaces";

import DistanceLabels from "./DistanceLabels";

import styles from "./map-polygon-builder.module.scss";

const MapPolygonBuilder = () => {
  const dispatch = useDispatch();

  const { unitsCollection, creationPolygonParameters } =
    useSelector<RootStateInterface, UnitsManagerStateInterface>(
      (state) => state.unitsManager
    );

  const isImpossibleToRemovePoint = useMemo(() => {
    return (
      creationPolygonParameters.id &&
      creationPolygonParameters?.geo_data?.coordinates.length === 3
    );
  }, [creationPolygonParameters]);

  const MarkerIcon = L.divIcon({
    html: renderToString(
      <div
        className={cn(styles["marker"], {
          [styles["marker-default"]]: isImpossibleToRemovePoint
        })}
      />
    ),
    iconSize: [16, 16],
    className: "marker-reset"
  });

  useEffect(() => {
    dispatch(
      features.unitsManager.actions.updateCreationPolygonParameters({
        ...creationPolygonParameters,
        name: `Poly ${
          unitsCollection[LayerAlias.MANUAL][UnitTypes.POLYGON].total + 1
        }`
      })
    );
    // eslint-disable-next-line
  }, [unitsCollection[LayerAlias.MANUAL][UnitTypes.POLYGON].total]);

  const updateMarkerPlacement = (index, newCoordinate) => {
    const updatedCoordinates =
      creationPolygonParameters.geo_data.coordinates.map((coord, i) =>
        i === index ? newCoordinate : coord
      );

    dispatch(
      features.unitsManager.actions.updateCreationPolygonParameters({
        ...creationPolygonParameters,
        geo_data: {
          ...creationPolygonParameters.geo_data,
          coordinates: updatedCoordinates
        }
      })
    );
  };

  const removePolygonPoint = useCallback(
    (indexToRemove) => {
      dispatch(
        features.unitsManager.actions.updateCreationPolygonParameters({
          ...creationPolygonParameters,
          geo_data: {
            ...creationPolygonParameters.geo_data,
            coordinates: [
              ...creationPolygonParameters.geo_data.coordinates.filter(
                (_, index) => index !== indexToRemove
              )
            ]
          }
        })
      );
    },
    [dispatch, creationPolygonParameters]
  );

  // You need to use a long key that will include all the polygon parameters that can change.
  const generatedPolygonKey = useMemo(
    () =>
      `${creationPolygonParameters.additional.stroke_color}-${creationPolygonParameters.additional.fill_color}-${creationPolygonParameters.additional.stroke_transparency}-${creationPolygonParameters.additional.fill_transparency}`,
    [
      creationPolygonParameters.additional.fill_color,
      creationPolygonParameters.additional.fill_transparency,
      creationPolygonParameters.additional.stroke_color,
      creationPolygonParameters.additional.stroke_transparency
    ]
  );

  return (
    <div className={styles["polygon-builder"]}>
      {creationPolygonParameters.geo_data.coordinates.length > 1 && (
        <Polygon
          key={generatedPolygonKey}
          positions={creationPolygonParameters.geo_data.coordinates}
          color={creationPolygonParameters.additional.stroke_color}
          fillColor={creationPolygonParameters.additional.fill_color}
          opacity={convertPercentagesToNumber(
            creationPolygonParameters.additional.stroke_transparency
          )}
          fillOpacity={convertPercentagesToNumber(
            creationPolygonParameters.additional.fill_transparency
          )}
        />
      )}

      {creationPolygonParameters.geo_data.coordinates.length > 0 &&
        creationPolygonParameters.geo_data.coordinates.map((point, index) => (
          <Marker
            key={index}
            position={point}
            icon={MarkerIcon}
            draggable={true}
            eventHandlers={{
              dragend: (event) => {
                const { lat, lng } = event.target.getLatLng();
                updateMarkerPlacement(index, [lat, lng]);
              },
              click: () => !isImpossibleToRemovePoint && removePolygonPoint(index)
            }}
          ></Marker>
        ))}
      {creationPolygonParameters.geo_data.coordinates.length > 1 && (
        <DistanceLabels
          points={creationPolygonParameters.geo_data.coordinates}
        />
      )}
    </div>
  );
};

export default MapPolygonBuilder;
